<template>
  <div>
    <RfPassword
      :loginError="loginError"
      @submit="onSetPassword"
      @keydown="showError && resetError()"
    />
    <RfBaseModal
      v-if="accountSuccessDialog"
      v-model="accountSuccessDialog"
      persistent
      width="600"
      title="Success!"
      :message="message"
      hideCloseButton
      overlayOpacity="0.85"
      primaryText="Continue"
      @keyup.enter="router.replace('/login')"
      @primary="router.replace('/login')"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import RfPassword from "@/components/RfPassword.vue";
import { useUserStore } from "@/pinia/userStore";
import { useLegacyLastErrorStore } from "@/pinia/legacyLastErrorStore";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";

const error = ref(null);
const showError = ref(false);
const accountSuccessDialog = ref(false);
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const legacyLastErrorStore = useLegacyLastErrorStore();

const isPasswordReset = computed(() => /password$/.test(route.path));
const isInvitation = computed(() => /invitation$/.test(route.path));

const message = computed(() => {
  if (isPasswordReset.value) return "Your password has been successfully updated.";
  if (isInvitation.value) return "Your account has been successfully created.";
  return "";
});

const displayError = err => {
  if (!err) return "";
  const message = err
    .toString()
    .replace(/[_\[]/g, " ")
    .replace(/^Error:/, "")
    .replace(/[\]"{}:]/g, "");
  return `Error: ${message}`;
};

const loginError = computed(() => {
  const errorString = error.value || displayError(legacyLastErrorStore.lastError);
  return showError.value && errorString !== "Error:  You must login to continue"
    ? errorString
    : null;
});

const onSetPassword = async ({ password, password_confirmation }) => {
  try {
    const response = await getResponse({ password, password_confirmation });
    if (response) accountSuccessDialog.value = true;
  } catch (e) {
    setError(e);
  }
};

const setError = err => {
  error.value = err;
  showError.value = true;
};

const resetError = () => {
  error.value = null;
  showError.value = false;
};

const getResponse = ({ password, password_confirmation }) => {
  if (isPasswordReset.value) {
    if (!route.query.reset_password_token) return setError("Reset password token is missing.");
    return userStore.resetPasswordUser({
      reset_password_token: route.query.reset_password_token,
      password,
      password_confirmation,
    });
  }

  if (isInvitation.value) {
    if (!route.query.invitation_token) return setError("Invitation token is missing.");
    return userStore.confirmInvitedUser({
      invitation_token: route.query.invitation_token,
      password,
      password_confirmation,
    });
  }

  return false;
};
</script>
